import * as React from "react";

import { noop } from "../../../../../../Common/TypingUtils";
import { createHocDisplayName, GetProps } from "../../../TypingUtils";

import { IUnitRegistry } from "./IUnitRegistry";
import { UnitRegistry } from "./UnitRegistry";

const emptyUnitRegistry: IUnitRegistry = {
    registerUnit: noop,
    unregisterUnit: noop,
    getUnitController: () => undefined,
};
const UnitRegistryContext = React.createContext<IUnitRegistry>(emptyUnitRegistry);

function useUnitRegistry(): IUnitRegistry {
    return React.useContext(UnitRegistryContext);
}

interface UnitRegistryInjectableProps {
    unitRegistry: IUnitRegistry;
}

interface UnitRegistryProviderProps {
    children: React.ReactNode;
    unitRegistry?: IUnitRegistry;
}

export function UnitRegistryProvider(props: UnitRegistryProviderProps): JSX.Element {
    const unitRegistry = React.useMemo<IUnitRegistry>(
        () => props.unitRegistry ?? new UnitRegistry(),
        [props.unitRegistry]
    );
    return <UnitRegistryContext.Provider value={unitRegistry}>{props.children}</UnitRegistryContext.Provider>;
}

export function withUnitRegistry<C extends React.ComponentType<GetProps<C> & UnitRegistryInjectableProps>>(
    WrappedComponent: C
): React.ComponentType<Omit<JSX.LibraryManagedAttributes<C, GetProps<C>>, "unitRegistry">> {
    function WithUnitRegistryWrapper(
        props: Omit<JSX.LibraryManagedAttributes<C, GetProps<C>>, "unitRegistry">
    ): JSX.Element {
        const unitRegistry = useUnitRegistry();
        // @ts-ignore Баг с HOC-ами https://github.com/Microsoft/TypeScript/issues/28938
        return <WrappedComponent {...props} unitRegistry={unitRegistry} />;
    }

    WithUnitRegistryWrapper.displayName = createHocDisplayName(WrappedComponent, "UnitRegistry");
    return WithUnitRegistryWrapper;
}
